/**
 * This component is used to show the collapsed state of shipping options on the checkout page. The details comprises of name, addresses, phone number, email etc.
 *
 * @module views/components/ShippingDetailsSplit
 * @memberof -Common
 */
import './ShippingDetailsSplit.scss';

import React, { useCallback, useEffect, useRef } from 'react';

import PropTypes from 'prop-types';

import AsyncComponent from '@ulta/core/components/AsyncComponent/AsyncComponent';
import Button from '@ulta/core/components/Button/Button';
import InlineMessage from '@ulta/core/components/InlineMessage/InlineMessage';
import Text from '@ulta/core/components/Text/Text';
import useLoader from '@ulta/core/hooks/useLoader/useLoader';
import { useOverlay } from '@ulta/core/providers/OverlayProvider/OverlayProvider';
import { useUserContext } from '@ulta/core/providers/UserContextProvider/UserContextProvider';
import { hasItems } from '@ulta/core/utils/array/array';
import { clientEvents } from '@ulta/core/utils/constants/events';
import { isServer } from '@ulta/core/utils/device_detection/device_detection';
import { spreadProps } from '@ulta/core/utils/dom/dom';
import { scrollToElement } from '@ulta/core/utils/scrollToElement/scrollToElement';

import BlockMessage from '@ulta/components/BlockMessage/BlockMessage';

import { useOnActionToggle } from '@ulta/hooks/useOnActionToggle/useOnActionToggle';

import * as utils from './ShippingDetailsSplit';
/**
 * Represents a ShippingDetailsSplit component
 *
 * @method
 * @param {ShippingDetailsSplitProps} props - React properties passed from composition
 * @returns ShippingDetailsSplit
 */
export const ShippingDetailsSplit = ( props ) => {
  const {
    componentKey,
    displayAddressForm,
    editAction,
    formattedShippingAddress,
    hasEvent,
    headline,
    invokeMutation,
    loading,
    messages,
    moduleName,
    publishEvent,
    shippingAddressForm,
    subscriptionShippingMessage,
    checkoutShipMessage
  } = props;

  const { user } = useUserContext();
  const { displayOverlay } = useOverlay();
  const [loader, showLoader, hideLoader] = useLoader( {} );
  const [onActionDispatched, showForm, setShowForm] = useOnActionToggle( { watch: displayAddressForm, loading } );
  const showSubscriptionShippingMessage = !showForm && subscriptionShippingMessage?.message;

  const enrichedCheckoutShipMessage = checkoutShipMessage ? { ...checkoutShipMessage } : null;
  if( enrichedCheckoutShipMessage?.action2?.graphql && enrichedCheckoutShipMessage?.action2?.params ){
    const moduleParams = {};
    if( enrichedCheckoutShipMessage.action2.params.includes( 'lineItemIds' ) && checkoutShipMessage.lineItemIds ){
      moduleParams.lineItemIds = checkoutShipMessage.lineItemIds;
    }
    enrichedCheckoutShipMessage.action2.variables = { moduleParams, url: { path: !isServer() && global.location.pathname } };
  }

  // Handle edges cases for open/close form here
  const handleExpandCollapse = useCallback(
    utils.composeExpandCollapseForm(
      { displayOverlay, displayAddressForm, showForm },
      { onActionDispatched, setShowForm }
    ),
    [displayOverlay, displayAddressForm, showForm, onActionDispatched, setShowForm]
  );

  useEffect( handleExpandCollapse, [handleExpandCollapse] );

  // Handle publishing event to enable/disable place order button
  const eventHandler = useCallback(
    utils.composeEventPublisher(
      { waitingForUserInput: showForm, clientActionParams: editAction?.clientActionParams, moduleName },
      { publishEvent, hasEvent }
    ),
    [showForm, publishEvent, hasEvent, editAction]
  );

  useEffect( eventHandler, [eventHandler] );

  // Handle showing messages and scrolling
  const wrapperRef = useRef();
  const handleHazmatErrorScrollToTop = useCallback( utils.composeScrollToMessage( { messages, wrapperRef } ) );

  useEffect( handleHazmatErrorScrollToTop, [handleHazmatErrorScrollToTop] );

  useEffect( () => {
    utils.composeHandleLoader( { loading }, { hideLoader, showLoader } );
  }, [loading] );

  const showEditButton = !showForm && editAction;

  useEffect( () => {
    if( showEditButton ){
      publishEvent( {
        event: clientEvents.FOCUS_MODULE,
        payload: { fulfillmentType: 'PaymentMethod' }
      } );
    }
  }, [showEditButton, publishEvent] );

  return (
    <div className='ShippingDetailsSplit'
      ref={ wrapperRef }
    >
      <div className='ShippingDetailsSplit__header'>
        { headline && (
          <Text
            textStyle='eyebrow'
            htmlTag='h3'
            color='neutral-500'
          >
            { headline }
          </Text>
        ) }

        { showEditButton && (
          <div className='ShippingDetailsSplit__header--button'>
            { user.loginStatus && editAction && (
              <Button
                withHover
                type='button'
                ariaLabel={ `${editAction.label} ${headline}` }
                label={ editAction.label }
                action={ editAction }
                iconImage='Edit'
                iconSize={ 's' }
                crossButtonVisibility={ true }
                tertiary
                tiny
                ariaHiddenIcon={ true }
                alignment='right'
                onClick={ onActionDispatched }
              />
            ) }

            { !user.loginStatus && editAction && (
              <Button
                withHover
                type='button'
                ariaLabel={ `${editAction.label} ${headline}` }
                label={ editAction.label }
                onClick={ () => setShowForm( true ) }
                iconImage='Edit'
                iconSize={ 's' }
                tertiary
                tiny
                ariaHiddenIcon={ true }
              />
            ) }
          </div>
        ) }
      </div>
      { loader }
      <ShippingDetailsSplitMessages messages={ messages }
        componentKey={ componentKey }
        checkoutShipMessage={ checkoutShipMessage }
        invokeMutation={ invokeMutation }
      />

      { !showForm && (
        <div className='ShippingDetailsSplit__shippingTo'>
          { formattedShippingAddress && (
            <div className='ShippingDetailsSplit__addressText'>
              <Text textStyle='body-2'>{ formattedShippingAddress }</Text>
            </div>
          ) }
        </div>
      ) }
      { showSubscriptionShippingMessage &&
      <div className='ShippingDetailsSplit__ShippingMessage'>
        <InlineMessage messageType={ subscriptionShippingMessage.icon }
          message={ subscriptionShippingMessage.message }
          role='status'
        />
      </div>
      }

      { showForm && (
        <AsyncComponent
          // Shipping address form DXL model
          { ...spreadProps( { ...shippingAddressForm } ) }
          // Client overrides
          componentKey={ componentKey }
          headline=''
          invokeMutation={ invokeMutation }
          loading={ loading }
          messages={ messages }
          moduleName='ShippingAddressFormSplit'
          setShowForm={ setShowForm }
        />
      ) }
    </div>
  );
};

/**
 * Represents a ShippingDetailsSplitMessages component
 * @param {object} props props passed to the component
 * @param {array} messages messages array DXL prop
 * @param {string} componentKey DXL prop for subTotal amount
 * @returns ShippingDetailsSplitMessages
 */
export const ShippingDetailsSplitMessages = ( props ) => {
  const { messages, componentKey, checkoutShipMessage, invokeMutation } = props;

  if( !hasItems( messages ) && !checkoutShipMessage ){
    return null;
  }

  return (
    <ul>
      { checkoutShipMessage && Object.keys( checkoutShipMessage ).length > 0 &&
        <li className='ShippingDetailsSplit__blockErrorMessage'
          key={ `${componentKey}-shipping-details-hazmat` }
        >
          <BlockMessage
            message={ checkoutShipMessage.message }
            messageType='warning'
            textColor={ checkoutShipMessage.textColor }
            invokeMutation={ invokeMutation }
            showCloseButton={ false }
            action1={ checkoutShipMessage.action1 }
            action2={ checkoutShipMessage.action2 }
            action1Style='link'
            action2Style={ checkoutShipMessage.action2?.graphql ? 'buttonAsLink' : 'link' }
            role='alert'
            items={ checkoutShipMessage.items }
          />
        </li> }
      { messages && messages.map( ( data, i ) => {
        if( !data ){
          return null;
        }

        const action1 = {};
        if( data.action1?.graphql ){
          action1.messageAction = data.action1;
        }
        else {
          action1.action1 = data.action1;
        }

        return (
          <li className='ShippingDetailsSplit__blockErrorMessage'
            key={ `${componentKey}-shipping-details-${i}` }
          >
            <BlockMessage
              message={ data.message }
              messageType={ data.icon }
              backgroundColor={ data.backgroundColor }
              borderColor={ data.borderColor }
              textColor={ data.textColor }
              dismissAction={ data.dismissAction }
              { ...spreadProps( { ...action1 } ) }
              action1Style='link'
              action2={ data.action2 }
              role='alert'
            />
          </li>
        );
      } ) }
    </ul>
  );
};

/**
 * composeExpandCollapseForm - is used to manage expanded/collapsed form
 * @param {object} data
 * @param {object} methods
 * @returns {function} calls setShowForm/onActionDispatched
 */
export const composeExpandCollapseForm = ( data, methods ) => () => {
  const { displayOverlay, displayAddressForm, showForm } = data || {};
  const { setShowForm, onActionDispatched } = methods || {};

  if( !setShowForm || !onActionDispatched ){
    return;
  }

  // Handles edge case where we dont know if an action has been dispatched or not
  // when the block message has an edit action in it
  if( !displayOverlay && displayAddressForm && !showForm ){
    onActionDispatched();
  }

  // Handles edge case where addressVerification drawer is open and we need to show
  // expanded shipping details form for anonymous user
  if( displayOverlay && displayAddressForm ){
    setShowForm( true );
  }

  // Handles edge case where shipping detail form is getting closed after
  // entering wrong address
  if( displayAddressForm && !showForm ){
    setShowForm( true );
  }

  if( displayOverlay && showForm ){
    onActionDispatched();
  }
};

/**
 * composeEventPublisher - is used to enable or disable the Place Order Button
 * @method composeEventPublisher
 * @param {object} data - Data arguments
 * @param {boolean} data.moduleName - Passing module name
 * @param {boolean} data.waitingForUserInput - true/false for enable or disable
 * @param {object} methods
 * @param {function} methods.publishEvent - To Publish the event to EventProvider
 * @returns {function} response handler callback
 */
export const composeEventPublisher = ( data, methods ) => () => {
  const { waitingForUserInput, moduleName } = data || {};
  const { publishEvent } = methods || {};

  if( !publishEvent || !moduleName ){
    return;
  }

  publishEvent( { event: clientEvents.CheckoutEnablement, payload: { moduleName, waitingForUserInput } } );

  return () => {
    publishEvent( { event: clientEvents.CheckoutEnablement, payload: { moduleName, waitingForUserInput: false } } );
  };
};

/**
 * composeScrollToMessage function to set error message when Hazmat Error is available
 * @method
 * @param {Object} data - Passing required data as an argument
 */
export const composeScrollToMessage = ( data ) => () => {
  const { messages, wrapperRef } = data || {};

  if( !hasItems( messages ) ){
    return;
  }

  scrollToElement( { el: wrapperRef?.current } );
};

/**
 * A click handler function for showing loading state based on the condition
 * @method
 * @param {Object} data - Passing data as an argument
 * @param {Object} methods - Passing methods as an argument
 */
export const composeHandleLoader = ( data, methods ) => {
  const { loading } = data || {};
  const { hideLoader, showLoader } = methods || {};

  if( !hideLoader || !showLoader ){
    return;
  }
  if( loading ){
    showLoader();
  }
  else {
    hideLoader();
  }
};

/**
 * Property type definitions
 * @typedef ShippingDetailsSplitProps
 * @type {object}
 * @property {string} headline - Sets the shipping details title
 * @property {string} subHeadline - Sets the shipping to label
 * @property {string} formattedShippingAddress - Sets the first name
 * @property {function} editAction - Sets the edit action for the edit button
 * @property {array} messages - Set the messages for the block message
 * @property {object} subscriptionShippingMessage - Sets the message that address user fills in will be saved for subscription
 * @property {object} checkoutShipMessage - Sets the messages for the block message in the checkout page for Hazmat items shipping
 */
export const propTypes = {
  /** Sets the shipping details title */
  headline: PropTypes.string.isRequired,
  /** Sets the shipping to label */
  subHeadline: PropTypes.string,
  /** Sets the formattedShippingAddress */
  formattedShippingAddress: PropTypes.string,
  /** Sets the edit action for the edit button */
  editAction: PropTypes.oneOfType( [PropTypes.func, PropTypes.object] ),
  /** Function to invoke a GraphQL mutation */
  invokeMutation: PropTypes.func,
  /** Sets the messages for the block message */
  messages: PropTypes.array,
  /** Sets the message that address user fills in will be saved for subscription */
  subscriptionShippingMessage: PropTypes.object,
  /** Sets the messages for the block message in the checkout page for Hazmat items shipping */
  checkoutShipMessage: PropTypes.object
};

ShippingDetailsSplit.propTypes = propTypes;

export default ShippingDetailsSplit;
